import "../../users/view-user/viewUser.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import useAuth from "../../../../hooks/useAuth";
import axiosInstance from "../../../../api/axios";
import ApplicationDetails from "../../users/view-user/applicationDetails";
import ApplicationNavigation from "../../users/view-user/applicationNavigation";
import { useUnsavedChanges } from "../../../../context/NavigationGuard";

const AssignedApplication = ({ index }) => {
  const navigate = useNavigate();
  const headingRef = useRef(null);
  const [email, setEmail] = useState("");
  const { authState } = useAuth();
  const [application, setApplication] = useState({});
  const [acceptOrDecline, setAD] = useState("");
  const [priorStatus, setPriorStatus] = useState("");
  const [travelReimbursement, setTravelReimbursement] = useState(false);
  const [housingAccommodations, setHousingAccommodations] = useState(false);
  const [oldTravelReimbursement, setOldTravelReimbursement] = useState(false);
  const [oldHousingAccommodations, setOldHousingAccommodations] =
    useState(false);
  const [teammates, setTeammates] = useState([]);
  const [noMore, setNoMore] = useState(false);
  const [status, setStatus] = useState("to_review");
  const [questions, setQuestions] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [retry, setRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState("");
  const hasChanges = !detailsLoading && !!application?.email && (
    acceptOrDecline !== priorStatus || travelReimbursement !== oldTravelReimbursement ||
    housingAccommodations !== oldHousingAccommodations
  );
  useUnsavedChanges(hasChanges, saving);
  const goTo = (path) => { if (!saving) navigate(path); };
  const navigationProps = {
    index: noMore ? -1 : Number(index), total, loading: detailsLoading, busy: saving,
    error: loadError, onRetry: () => setRetry((value) => value + 1),
    onPrevious: () => goTo(`/admin/reviewapps/${Number(index) - 1}`),
    onNext: () => goTo(`/admin/reviewapps/${Number(index) + 1}`),
    onBack: () => goTo("/admin/reviewapps"),
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    let active = true;
    const fetchStatus = async () => {
      if (!authState.user?.email) {
        return;
      }

      setDetailsLoading(true);
      setLoadError("");
      try {
        const assignedResponse = await axiosInstance.get(
          `/api/applications/assigned-applications/${authState.user.email}`
        );
        if (!active) return;
        const assigned = assignedResponse.data.assigned || [];
        setTotal(assigned.length);
        const current = Number.isInteger(Number(index)) ? assigned[Number(index)] : null;

        if (!current) {
          setNoMore(true);
          setDetailsLoading(false);
          return;
        }

        setNoMore(false);
        setEmail(current.email);
        setStatus(current.status);

        const [statusResponse, applicationResponse, questionsResponse] =
          await Promise.all([
            axiosInstance.get(
              `/api/applications/status/${current.email}`
            ),
            axiosInstance.get(`/api/applications/${current.email}`),
            axiosInstance.get("/api/applications/questions"),
          ]);
        if (!active) return;

        setAD(statusResponse.data["status"]);
        setPriorStatus(statusResponse.data["status"]);
        setTravelReimbursement(statusResponse.data["travel_reimbursement"]);
        setOldTravelReimbursement(statusResponse.data["travel_reimbursement"]);
        setHousingAccommodations(statusResponse.data["housing_accommodations"]);
        setOldHousingAccommodations(statusResponse.data["housing_accommodations"]);

        setQuestions(questionsResponse.data || []);

        if (applicationResponse.data?.hasOwnProperty("teamcode")) {
          const teammates_response = await axiosInstance.get(
            `/api/applications/teammates/${applicationResponse.data.teamcode}`
          );
          if (!active) return;
          setTeammates(teammates_response.data.emails);
        }

        setApplication(applicationResponse.data || {});
      } catch (err) {
        if (active) setLoadError("Unable to load this review. Please try again.");
      } finally {
        if (active) setDetailsLoading(false);
      }
    };

    fetchStatus();
    return () => { active = false; };
  }, [authState.user?.email, index, retry]);

  const handleDownload = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.get(
        `/api/resume/download/${email}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${email}_resume.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setSaveError("Unable to download the resume. Please try again.");
    }
  };

  const handleSelectChange = (event) => {
    setAD(event.target.value);
  };

  const handleStatusChange = async (e) => {
    const nextStatus = e.target.value;
    setSaving(true);
    setSaveError("");
    setSaveMessage("");
    try {
      await axiosInstance.put(
        `/api/applications/change-review-status/${authState.user.email}/${email}/${nextStatus}`
      );

      setStatus(nextStatus);
      setSaveMessage("Review status saved.");
    } catch (err) {
      setSaveError("Unable to save the review status. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitStatus = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaveError("");
    setSaveMessage("");
    try {
      await axiosInstance.post(
        `/api/applications/status/${email}`,
        {
          status: acceptOrDecline,
          travel_reimbursement: travelReimbursement,
          housing_accommodations: housingAccommodations,
        }
      );

      setPriorStatus(acceptOrDecline);
      setOldTravelReimbursement(travelReimbursement);
      setOldHousingAccommodations(housingAccommodations);
      setSaveMessage("Decision saved.");
    } catch (err) {
      setSaveError(err.response?.data?.message || "Unable to save the decision. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="view-user-page">
      {noMore && <h1 className="no-more-h1">No More to Review</h1>}

      <div className="dashboard">
        <div className="vu-review-navigation"><ApplicationNavigation {...navigationProps} position="top" /></div>
        <div className="title-div">
          <div>
            <h1 className="user-dash-title" ref={headingRef} tabIndex={-1}>
              User: {application.first} {application.last}
            </h1>
            <p>Email: {email}</p>
            <p>Current Status: {priorStatus}</p>
            <p>Travel Reimbursement: {oldTravelReimbursement ? "Yes" : "No"}</p>
            <p>
              Housing Accommodations: {oldHousingAccommodations ? "Yes" : "No"}
            </p>
            <div className="vu-actions">
              <label className="label-vu" htmlFor="review-status">Review status</label>
              <select
                id="review-status"
                className="user-dash-b"
                name="ad"
                value={status}
                onChange={handleStatusChange}
                disabled={saving || detailsLoading || !!loadError || noMore}
              >
                <option value="to_review">To Review</option>
                <option value="complete">Complete</option>
              </select>
            </div>
            {hasChanges && <p role="status" className="vu-unsaved">Unsaved changes</p>}
            {saveMessage && <p role="status" className="vu-inline-message">{saveMessage}</p>}
            {saveError && <p role="alert" className="vu-inline-error">{saveError}</p>}
          </div>
        </div>

        <div className="title-div">
          <div className="left-div">
            <h1 className="user-dash-title">Tools</h1>
          </div>
          <div className="right-div">
            <div>
              <form onSubmit={handleSubmitStatus} className="vu-form">
                <fieldset className="vu-decision-fields" disabled={saving || detailsLoading || !!loadError || noMore}>
                <div className="vu-labelselect">
                  <label className="label-vu" htmlFor="review-decision">Decision</label>
                  <select
                    id="review-decision"
                    className="confirm-deadline"
                    name="ad"
                    value={acceptOrDecline}
                    onChange={handleSelectChange}
                  >
                    <option value="submitted">No Decision</option>
                    <option value="accepted">Accepted</option>
                    <option value="waitlisted">Waitlisted</option>
                    <option value="rejected">Rejected</option>
                    <option value="waitlist_accepted">Waitlist Accepted</option>
                  </select>
                </div>
                <div className="vu-labelselect">
                  <label className="label-vu" htmlFor="review-travel">Travel Reimbursement</label>
                  <select
                    id="review-travel"
                    className="confirm-deadline"
                    value={travelReimbursement}
                    onChange={(e) =>
                      setTravelReimbursement(e.target.value === "true")
                    }
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
                <div className="vu-labelselect">
                  <label className="label-vu" htmlFor="review-housing">Housing Accommodations</label>
                  <select
                    id="review-housing"
                    className="confirm-deadline"
                    value={housingAccommodations}
                    onChange={(e) =>
                      setHousingAccommodations(e.target.value === "true")
                    }
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
                <button type="submit" className="user-dash-b">
                  {saving ? "Saving..." : "Save Decision"}
                </button>
                </fieldset>
              </form>
            </div>
            <div>
              <label className="label-vu">Download User's Resume</label>
              <button
                onClick={handleDownload}
                className="button-vu"
                type="submit"
              >
                Download Resume
              </button>
            </div>
          </div>
        </div>

        <div className="title-div">
          <div className="left-div">
            <h1 className="user-dash-title">Team Information</h1>
          </div>
          <div className="right-div">
            <div>
              <h2 className="ad-vu-scores-title">
                Team Code: {application.teamcode}
              </h2>
            </div>
            <div>
              <h2 className="ad-vu-scores-title">Team</h2>
              <ul className="ad-vu-list">
                {teammates.map((email, index) => (
                  <li key={index} className="ad-vu-score">
                    <Link className="ad-vu-score-a" to={`/admin/users/${encodeURIComponent(email)}`}>
                      {email}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* <div className="title-div">
          <div className="left-div">
            <h1 className="user-dash-title">Score User</h1>
          </div>
          <div className="right-div">
            <div>
              <form className="ad-vu-form">
                <input
                  type="numeric"
                  className="ad-vu-enterscore"
                  placeholder="Enter Score"
                />
                <button type="submit" className="user-dash-b">
                  Submit Score
                </button>
              </form>
            </div>
          </div>
          <div>
            <h2 className="ad-vu-scores-title">Current Scores</h2>
            <ul className="ad-vu-list">
              <li className="ad-vu-score">Mark: 6</li>
              <li className="ad-vu-score">Mark: 6</li>
              <li className="ad-vu-score">Mark: 6</li>
            </ul>
          </div>
          <div className="ad-vu-averagescore">
            <h2 className="ad-vu-scores-title">Average Score</h2>
            <p className="ad-vu-average-score-p">6.0</p>
          </div>
        </div> */}

        {/* <div className="title-div">
          <div className="left-div">
            <h1 className="user-dash-title">Confirmation Details</h1>
          </div>
          <div className="right-div">
            <div>
              <h2 className="contact-info-h1">Contact</h2>
              <p className="email-p">Email: {email}</p>
            </div>
          </div>
        </div> */}

        <div className="bottom-div">
          <h1 className="user-dash-title-bd">Application Details</h1>
          <ApplicationDetails
            application={application}
            questions={questions}
            loading={detailsLoading}
          />
          <ApplicationNavigation {...navigationProps} position="bottom" />
        </div>
      </div>
    </div>
  );
};

const ViewFromOps = () => {
  const { index } = useParams();
  return <AssignedApplication key={index} index={index} />;
};

export default ViewFromOps;
